// Feb 2026 · R280.3 · Front-desk QR scan app.
// Kiosk-style page for studio staff to scan a customer's Arrival QR
// and be taken straight to their live booking tracker. Uses
// `html5-qrcode` (v2.3.8) — the same lib PagerDuty + Twilio-status
// pages use — because it handles camera enumeration, permission
// prompts, torch, and re-focus loops on both mobile Safari and
// desktop Chrome without extra plumbing.
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, QrCode, ExternalLink, ArrowLeft, ScanLine } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function StudioScan() {
  const scannerRef = useRef(null);
  const [instance, setInstance] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | scanning | error
  const [error, setError] = useState("");
  const [decoded, setDecoded] = useState(null);
  const [history, setHistory] = useState([]);

  const start = async () => {
    setError(""); setDecoded(null);
    try {
      // Lazy-import so the ~200KB html5-qrcode chunk only loads for
      // staff who actually visit the scan page.
      const { Html5Qrcode, Html5QrcodeSupportedFormats } = await import("html5-qrcode");
      if (instance) { try { await instance.stop(); await instance.clear(); } catch (_) {} }
      const el = document.getElementById("sb-scan-reader");
      if (!el) return;
      const scanner = new Html5Qrcode("sb-scan-reader", {
        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
        verbose: false,
      });
      setInstance(scanner);
      await scanner.start(
        { facingMode: "environment" },
        { fps: 12, qrbox: (vw, vh) => {
            const min = Math.min(vw, vh);
            const size = Math.floor(min * 0.7);
            return { width: size, height: size };
          },
          aspectRatio: 1.0,
          disableFlip: false,
        },
        (text) => onScan(text, scanner),
        () => { /* per-frame decode misses — noop */ }
      );
      setStatus("scanning");
    } catch (e) {
      setStatus("error");
      setError(e?.message || "Couldn't start the camera. Grant access from the address bar.");
    }
  };

  const stop = async () => {
    if (instance) {
      try { await instance.stop(); await instance.clear(); } catch (_) {}
      setInstance(null);
    }
    setStatus("idle");
  };

  const onScan = async (text, scanner) => {
    if (!text) return;
    // Only accept URLs that point at our tracker route. Guards against
    // random posters/WhatsApp QRs at the front desk from opening
    // arbitrary sites in the kiosk browser.
    let url;
    try { url = new URL(text); } catch { return; }
    if (!/\/track\/[^/]+\/[^/?#]+/.test(url.pathname)) {
      setError("This QR isn't a booking ticket — scan the one on the guest's email.");
      setDecoded({ url: text, ok: false });
      return;
    }
    setDecoded({ url: text, ok: true });
    setHistory((h) => [{ url: text, at: new Date().toISOString() }, ...h].slice(0, 6));
    // Stop the camera before navigating so the video stream releases.
    try { await scanner.stop(); await scanner.clear(); } catch (_) {}
    setInstance(null);
    setStatus("idle");
    // Small delay so the "Captured" flash animates before we jump.
    setTimeout(() => { window.location.href = text; }, 450);
  };

  useEffect(() => {
    // Auto-start on mount for a lightning-fast kiosk experience.
    start();
    return () => {
      if (instance) {
        try { instance.stop(); instance.clear(); } catch (_) {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#241A11] text-[#FBF7F0] flex flex-col" data-testid="sb-scan-page">
      <header className="flex items-center justify-between px-4 py-3 border-b border-[#3B2D1E]">
        <Link to="/app/studio" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[#C8B590] hover:text-white no-underline"
                data-testid="sb-scan-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Studio
        </Link>
        <div className="inline-flex items-center gap-2">
          <QrCode className="w-4 h-4 text-[#B8860B]" />
          <div className="text-[10px] uppercase tracking-[3px] font-semibold">Front-desk scan</div>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-4">
            <h1 className="text-3xl font-normal" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Scan the guest's ticket
            </h1>
            <p className="text-[13px] text-[#C8B590] mt-1">
              Point the camera at the QR from their booking email.
              We'll open the live tracker instantly.
            </p>
          </div>

          <div className="relative bg-black border-2 border-[#B8860B] aspect-square overflow-hidden"
                  data-testid="sb-scan-viewport">
            <div id="sb-scan-reader" ref={scannerRef} className="w-full h-full" />
            {status !== "scanning" && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/70">
                <div className="text-center px-6">
                  <Camera className="w-10 h-10 mx-auto text-[#B8860B] mb-2" />
                  <div className="text-sm font-semibold uppercase tracking-widest">
                    {status === "error" ? "Camera blocked" : "Tap start"}
                  </div>
                  <div className="text-[11px] text-[#C8B590] mt-1 max-w-xs">
                    {error || "Grant camera access to begin scanning."}
                  </div>
                  <Button onClick={start}
                              data-testid="sb-scan-start"
                              className="mt-3 rounded-none bg-[#B8860B] hover:bg-[#a17608] text-white uppercase tracking-widest text-[11px]">
                    Start camera
                  </Button>
                </div>
              </div>
            )}
            {decoded && decoded.ok && (
              <div className="absolute inset-x-0 bottom-0 bg-emerald-700/95 text-white px-3 py-2 text-[11px] font-semibold uppercase tracking-widest inline-flex items-center gap-2"
                      data-testid="sb-scan-captured">
                <ScanLine className="w-4 h-4" /> Captured — opening tracker…
              </div>
            )}
          </div>

          {decoded && !decoded.ok && (
            <div className="mt-3 border border-rose-400 bg-rose-500/20 p-3 text-[12px]"
                    data-testid="sb-scan-rejected">
              <div className="font-semibold uppercase tracking-widest text-rose-200">Not a booking ticket</div>
              <div className="text-rose-100 mt-1">Only guest arrival QRs are accepted. Retry from a booking email.</div>
            </div>
          )}

          <div className="mt-4 flex items-center gap-2 justify-center">
            {status === "scanning" ? (
              <Button variant="outline" onClick={stop}
                        data-testid="sb-scan-stop"
                        className="rounded-none border-[#C8B590] text-[#C8B590] bg-transparent uppercase tracking-widest text-[10px] hover:bg-[#3B2D1E]">
                Pause scanner
              </Button>
            ) : status !== "error" && (
              <Button onClick={start}
                        data-testid="sb-scan-resume"
                        className="rounded-none bg-[#B8860B] hover:bg-[#a17608] text-white uppercase tracking-widest text-[10px]">
                Resume scanning
              </Button>
            )}
          </div>

          {history.length > 0 && (
            <div className="mt-6 border border-[#3B2D1E] p-3">
              <div className="text-[10px] uppercase tracking-[3px] text-[#C8B590] font-semibold mb-2">
                Recent scans
              </div>
              <ul className="space-y-1.5" data-testid="sb-scan-history">
                {history.map((h, i) => (
                  <li key={i} className="text-[11px] flex items-center gap-2">
                    <ExternalLink className="w-3 h-3 text-[#B8860B] shrink-0" />
                    <a href={h.url} className="text-[#FBF7F0] hover:text-[#B8860B] no-underline truncate flex-1">
                      {h.url.replace(/^https?:\/\/[^/]+/, "")}
                    </a>
                    <span className="text-[#8A7A66]">{new Date(h.at).toLocaleTimeString()}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-center text-[10px] uppercase tracking-[3px] text-[#8A7A66] mt-6">
            Kiosk-friendly · works on any staff device — no install
          </p>
        </div>
      </main>
    </div>
  );
}
